import { ItemStatus } from '@/constants/item'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { productApi } from '../api/productApi'
import type { Product, ProductCategory } from '../types/product'

export function useGetProducts(
  params?: { categoryId?: string; excludeDepartmentId?: string; search?: string },
  enabled = true,
) {
  return useQuery({
    queryKey: ['products', params],
    queryFn: () => productApi.getProducts(params),
    enabled: enabled,
    retry: false,
    refetchOnWindowFocus: false,
    // staleTime: 1000 * 60 * 5,
  })
}

export function useGetProduct(id?: string, enabled = true) {
  return useQuery({
    queryKey: ['product', id],
    queryFn: () => productApi.getProduct(id!),
    enabled: enabled && !!id,
  })
}

export function useCreateProduct() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ payload }: { payload: Partial<Product> }) => productApi.createProduct(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
    },
  })
}

export function useUpdateProduct() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<Product> }) => productApi.updateProduct(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['product'] })
    },
  })
}

export function useDeleteProduct() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => productApi.deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
    },
  })
}

export function useGetProductItems(id: string, params?: { status?: ItemStatus; search?: string }, enabled = true) {
  return useQuery({
    queryKey: ['product-items', id, params],
    queryFn: () => productApi.getProductItems(id, params),
    retry: false,
    refetchOnWindowFocus: false,
    enabled,
  })
}

export function useGetProductCategories(params: { IsDeleted?: boolean } = { IsDeleted: false }, enabled = false) {
  return useQuery({
    queryKey: ['product-categories', params],
    queryFn: () => productApi.getProductCategories(params),
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
  })
}

export function useCreateProductCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ payload }: { payload: Partial<ProductCategory> }) => productApi.createProductCategory(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-categories'] })
    },
  })
}

export function useUpdateProductCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<ProductCategory> }) =>
      productApi.updateProductCategory(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-categories'] })
    },
  })
}

export function useDeleteProductCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => productApi.deleteProductCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-categories'] })
    },
  })
}
